// Photos and files (docs/FILES.md, Phase 6): upload the raw bytes first (you get an id), then send the message with `files: [ids]`.
// Images show inline, anything else downloads. Limits: 10 MB a file, 5 a message; the type is judged on the server from the bytes.
import { config } from './config';
import { getCsrf } from './client';

export interface FileRef { id: string; name: string; type: string; size: number; image: boolean }
/** GET /tickets/:id/files rows. */
export interface TicketFile extends FileRef { on: 'request' | 'reply' | 'update' | 'ticket'; message: string | null; published: boolean; by: { name: string }; at: number }

export const MAX_BYTES = 10 * 1024 * 1024;
export const MAX_FILES = 5;
/** What the file chooser offers. Listing JPEG and PNG (not image/*) makes an iPhone hand over JPEG instead of HEIC. */
export const ACCEPT = 'image/png,image/jpeg,image/gif,image/webp,application/pdf';
const OK_TYPES = /^(image\/(png|jpeg|gif|webp|heic|heif)|application\/pdf)$/;
const OK_EXT = /\.(png|jpe?g|gif|webp|heic|heif|pdf)$/i;

export const WORDS: Record<string, string> = {
  bad_type: 'Only photos (PNG, JPEG, GIF, WebP) and PDFs can be attached.',
  too_big: 'That file is too big. The most is 10 MB.',
  too_many_files: 'At most 5 files on one message.',
  too_many_uploads: 'Too many files are waiting to be sent. Send or remove some first.',
  bad_files: 'One of the files could not be attached. Remove it and add it again.',
  sent: 'The client already has that file, so it cannot be removed.',
  no_file: 'That file is not here any more.',
  rate_limited: 'Too many tries. Wait a moment.',
};

/** A friendly reason a file cannot be added, or null. Checked here so a phone never uploads something the server will refuse. */
export function problemWith(f: { name: string; type: string; size: number }): string | null {
  if (f.size === 0) return `${f.name} is empty.`;
  if (f.size > MAX_BYTES) return `${f.name} is too big. The most is 10 MB.`;
  if (!OK_TYPES.test(f.type) && !OK_EXT.test(f.name)) return `${f.name} is not a photo or a PDF.`;
  return null;
}

export const sizeText = (n: number) => (n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1000))} KB`);

/** Where the bytes are read: the portal's door for a customer, the team's for everyone else. */
export const fileUrl = (portal: boolean, id: string, _download = false) => `${config.apiBase}${portal ? '/portal' : ''}/files/${encodeURIComponent(id)}`;

/** The demo's files live in this tab: an object URL by id (mock/files.ts registers them). */
const demo = new Map<string, string>();
const refs = new Map<string, FileRef>();
export const registerDemoFile = (id: string, url: string, ref?: FileRef) => { demo.set(id, url); if (ref) refs.set(id, ref); };
/** The demo's File for an id it handed out (what a message sent with `files: [id]` carries). */
export const demoRef = (id: string): FileRef | undefined => refs.get(id);
export const viewUrl = (portal: boolean, f: FileRef) => (config.mock ? (demo.get(f.id) ?? '') : fileUrl(portal, f.id));

/** POST the raw bytes with progress (fetch cannot report upload progress). Resolves the File, or throws an Error whose message is plain words. */
export function upload(portal: boolean, file: File, onProgress: (done: number) => void): Promise<FileRef> {
  if (config.mock) {
    return new Promise((resolve, reject) => {
      const bad = problemWith(file);
      if (bad) return reject(new Error(bad));
      let n = 0;
      const t = setInterval(() => {
        n += 0.2;
        onProgress(Math.min(n, 1));
        if (n >= 1) {
          clearInterval(t);
          const ref: FileRef = { id: `fl_demo${Math.random().toString(16).slice(2, 12)}`, name: file.name, type: file.type || 'application/octet-stream', size: file.size, image: /^image\/(png|jpeg|gif|webp)$/.test(file.type) };
          registerDemoFile(ref.id, URL.createObjectURL(file), ref);
          resolve(ref);
        }
      }, 70);
    });
  }
  return new Promise((resolve, reject) => {
    const x = new XMLHttpRequest();
    x.open('POST', `${config.apiBase}${portal ? '/portal' : ''}/files?name=${encodeURIComponent(file.name)}`);
    x.withCredentials = true;
    x.setRequestHeader('content-type', file.type || 'application/octet-stream');
    const csrf = getCsrf();
    if (csrf) x.setRequestHeader('x-deleg8-csrf', csrf);
    x.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(e.loaded / e.total); };
    x.onerror = () => reject(new Error('Could not reach Deleg8. Check your connection and try again.'));
    x.onload = () => {
      let j: { error?: string; code?: string } & Partial<FileRef> = {};
      try { j = JSON.parse(x.responseText); } catch { /* none */ }
      if (x.status >= 200 && x.status < 300 && j.id) { onProgress(1); resolve(j as FileRef); return; }
      reject(Object.assign(new Error((j.code && WORDS[j.code]) || j.error || (x.status === 413 ? WORDS['too_big']! : 'That did not upload. Try again.')), { code: j.code }));
    };
    x.send(file);
  });
}
